import React from 'react'
import { type Column } from './utils'
import { TranslationKey } from '@/modules/i18n/types'
import { useTar } from '@/modules'
import { MultiPick } from '@duck/ui'
import type { PartnerService } from '@duck/api-definitions'

interface OrganizationTableFiltersProps {
    filterText: string
    setFilterText: (text: string) => void
    typeFilter: PartnerService.OrganizationType[]
    setTypeFilter: (types: PartnerService.OrganizationType[]) => void
    activeFilter: boolean | null
    setActiveFilter: (active: boolean | null) => void
    columns: Column[]
    columnDropdownOpen: boolean
    setColumnDropdownOpen: (open: boolean) => void
    toggleColumnVisibility: (columnKey: string) => void
}

const typeOptions: { value: PartnerService.OrganizationType, label: TranslationKey }[] = [
    { value: 'company', label: 'common.company' },
    { value: 'school', label: 'common.school' },
]

const activeOptions: { value: string, label: TranslationKey }[] = [
    { value: 'true', label: 'common.active' },
    { value: 'false', label: 'common.inactive' },
]

export const OrganizationTableFilters: React.FC<OrganizationTableFiltersProps> = ({
    filterText,
    setFilterText,
    typeFilter,
    setTypeFilter,
    activeFilter,
    setActiveFilter,
    columns,
    columnDropdownOpen,
    setColumnDropdownOpen,
    toggleColumnVisibility,
}) => {
    const { t } = useTar()

    const handleActiveFilterChange = (selected: string[]) => {
        if (selected.length === 0) {
            setActiveFilter(null)
        } else if (selected.includes('true') && selected.includes('false')) {
            setActiveFilter(null)
        } else if (selected.includes('true')) {
            setActiveFilter(true)
        } else {
            setActiveFilter(false)
        }
    }

    const activeFilterSelected = activeFilter === null
        ? []
        : activeFilter === true
            ? ['true']
            : ['false']

    return (
        <div className="space-y-4">
            {/* Primary Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                {/* Search Input */}
                <div className="flex-1 min-w-0">
                    <label htmlFor="search" className="sr-only">
                        {t('common.search')}
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            id="search"
                            type="text"
                            placeholder={t('common.searchByName')}
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            className="block w-full ps-10 pe-3 py-2 border border-border rounded-md leading-5 bg-card placeholder-secondary text-foreground focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                        />
                    </div>
                </div>

                {/* Type Filter */}
                <div className="w-full sm:w-64">
                    <label htmlFor="type-filter" className="sr-only">
                        {t('common.filterByType')}
                    </label>
                    <MultiPick
                        options={typeOptions.map(o => ({ value: o.value, label: t(o.label) }))}
                        selected={typeFilter}
                        onChange={(next) => setTypeFilter(next as PartnerService.OrganizationType[])}
                        labels={{ placeholder: t('common.allTypes') }}
                        className="py-[6px]"
                    />
                </div>

                {/* Active Status Filter */}
                <div className="w-full sm:w-64">
                    <label htmlFor="active-filter" className="sr-only">
                        {t('common.filterByStatus')}
                    </label>
                    <MultiPick
                        options={activeOptions.map(o => ({ value: o.value, label: t(o.label) }))}
                        selected={activeFilterSelected}
                        onChange={handleActiveFilterChange}
                        labels={{ placeholder: t('common.allStatuses') }}
                        className="py-[6px]"
                    />
                </div>

                {/* Column Visibility Dropdown */}
                <div className="w-full sm:w-64">
                    <label className="sr-only">
                        {t('common.columns')}
                    </label>
                    <MultiPick
                        options={columns.map(col => ({ value: col.key, label: t(col.label) }))}
                        selected={columns.filter(c => c.visible).map(c => c.key)}
                        onChange={(nextSelected) => {
                            const selectedSet = new Set(nextSelected)
                            columns.forEach(col => {
                                const shouldBeVisible = selectedSet.has(col.key)
                                if (shouldBeVisible !== col.visible) {
                                    toggleColumnVisibility(col.key)
                                }
                            })
                        }}
                        labels={{ placeholder: t('common.columns') }}
                        className="py-[6px]"
                    />
                </div>
            </div>
        </div>
    )
}

